/**
 * The only place the website talks to the network.
 *
 * Authentication is a server-set HttpOnly cookie: every request runs with
 * `credentials: 'include'` and the website never reads, stores or forwards a
 * token. Sign-in is a full browser navigation to the API's Discord route.
 *
 * Every answer is validated against `@garmoth/community-contracts`, so a
 * drifting API surfaces as one honest error state instead of a broken render.
 */
import type { z } from 'zod';

const configured = import.meta.env?.VITE_COMMUNITY_API_URL?.trim();
export const apiBaseUrl = (configured || 'http://127.0.0.1:23101').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The visitor is signed out, or the session cookie expired. */
  get isUnauthorized() {
    return this.status === 401;
  }

  /** The request never reached the API, or the answer did not match the contract. */
  get isUnreachable() {
    return this.status === 0;
  }
}

type QueryValue = string | number | boolean | undefined | null;

export function buildUrl(path: string, query?: Record<string, QueryValue>) {
  const url = new URL(`${apiBaseUrl}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString();
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
}

/** Sends one credentialed request and parses the answer with the contract schema. */
export async function request<Schema extends z.ZodType>(
  path: string,
  schema: Schema,
  options: RequestOptions = {},
): Promise<z.infer<Schema>> {
  const { method = 'GET', query, body, signal } = options;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      signal,
      credentials: 'include',
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, `The community API at ${apiBaseUrl} did not answer.`);
  }

  const payload: unknown = response.status === 204 ? undefined : await response.json().catch(() => undefined);
  if (!response.ok) throw new ApiError(response.status, errorMessage(payload, response.status));

  const parsed = schema.safeParse(payload);
  if (!parsed.success) throw new ApiError(0, 'The community API sent an answer this version does not understand.');
  return parsed.data;
}

function errorMessage(payload: unknown, status: number) {
  if (payload && typeof payload === 'object') {
    const message = 'error' in payload ? payload.error : 'message' in payload ? payload.message : null;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (status === 401) return 'Sign in with Discord to continue.';
  if (status === 403) return 'You do not have access to this.';
  if (status === 404) return 'That does not exist.';
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  return `The community API answered with ${status}.`;
}

/** Discord returns to the fixed site origin; preserve only a local path in this tab. */
export function startDiscordSignIn() {
  sessionStorage.setItem('garmoth-return-path', `${window.location.pathname}${window.location.search}`);
  window.location.assign(buildUrl('/auth/discord/start'));
}

/** WebSocket URL on the same origin as the API, for the live party roster. */
export function socketUrl(path: string) {
  const url = new URL(`${apiBaseUrl}${path}`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}
